import { getImageStore, setImageStore, clearAllImageStores } from "./imageStore";

const IMAGE_HOLDER_KEY = "imageHolder";
const TIER_IMAGE_KEY_PREFIX = "tierImages_";

export interface ImageItem {
	id: number;
	url: string;
	label?: string;
}

export interface Tier {
	id: number;
	color: string;
	tierLabel: string;
}

export interface Style {
	ratio: string;
	size: number;
	quality: number;
	pasteScaleMode: "fixed" | "preserve";
}

export interface ExportData {
	version: number;
	tiers: Tier[];
	style: Style;
	images: Record<string, ImageItem[]>;
}

export const exportTiersToJson = async (tiers: Tier[], style: Style): Promise<void> => {
	const images: Record<string, ImageItem[]> = {};

	const imageHolderImages = await getImageStore(IMAGE_HOLDER_KEY);
	if (imageHolderImages.length > 0) {
		images[IMAGE_HOLDER_KEY] = imageHolderImages;
	}

	for (const tier of tiers) {
		const key = `${TIER_IMAGE_KEY_PREFIX}${tier.id}`;
		const tierImages = await getImageStore(key);
		if (tierImages.length > 0) {
			images[key] = tierImages;
		}
	}

	const exportData: ExportData = {
		version: 1,
		tiers,
		style,
		images,
	};

	const json = JSON.stringify(exportData, null, 2);
	const blob = new Blob([json], { type: "application/json" });
	const url = URL.createObjectURL(blob);

	const link = document.createElement("a");
	link.href = url;
	const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
	link.download = `tiersofjoy-tierlist-${timestamp}.json`;
	link.click();

	URL.revokeObjectURL(url);
};

export const parseImportFile = (file: File): Promise<ExportData> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			let data: unknown;
			try {
				data = JSON.parse(reader.result as string);
			} catch {
				reject(new Error("Invalid JSON file"));
				return;
			}

			if (typeof data !== "object" || data === null) {
				reject(new Error("Invalid export file: not an object"));
				return;
			}

			const obj = data as Record<string, unknown>;
			if (!Array.isArray(obj.tiers)) {
				reject(new Error("Invalid export file: missing tiers array"));
				return;
			}

			resolve(data as ExportData);
		};

		reader.onerror = () => reject(new Error("Failed to read file"));
		reader.readAsText(file);
	});
};

export const restoreImportData = async (data: ExportData): Promise<void> => {
	await clearAllImageStores();

	if (data.images) {
		for (const [key, images] of Object.entries(data.images)) {
			await setImageStore(key, images);
		}
	}
};
